import { LoadingUI, Text } from 'basic-ui-lib';
import { LayoutCode } from 'modules/channel/services/constants';
import { IScreenDetail } from 'modules/dashboard/interfaces/api';
import dashboardApis from 'modules/dashboard/services/apis';
import { MediaType } from 'modules/media/interfaces/api';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { isMp4Url } from 'utils/isMp4Url';

const PreviewScreenPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [loading, setLoading] = useState(false);

  const [screenDetail, setScreenDetail] = useState<IScreenDetail | null>(null);
  const [index, setIndex] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!id || key) return;

    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await dashboardApis.getScreenDetail(id);

        if (res?.data?.data) {
          setScreenDetail(res.data.data as IScreenDetail);
        }
      } catch (error) {
        console.error('Error fetching screen detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id, key]);

  const data = useMemo(() => {
    if (key) {
      try {
        return JSON.parse(sessionStorage.getItem(key) || 'null');
      } catch {
        return null;
      }
    }

    return id ? screenDetail : null;
  }, [key, id, screenDetail]);

  const contentItems = data?.contentItems ?? [];
  const layoutCode = data?.layoutCode;
  const isLandscape = layoutCode === LayoutCode.MAIN_ONLY;

  const currentPlaylistItem = contentItems[index];

  const isVideo =
    currentPlaylistItem?.type === MediaType.VIDEO ||
    (currentPlaylistItem?.type === MediaType.FILE &&
      isMp4Url(currentPlaylistItem.url));

  const isImage =
    currentPlaylistItem?.type === MediaType.IMAGE ||
    (currentPlaylistItem?.type === MediaType.FILE &&
      !isMp4Url(currentPlaylistItem.url));

  const isWebView = currentPlaylistItem?.type === MediaType.WEBVIEW;

  const goNext = () => {
    setIndex((prev) => (prev + 1) % (contentItems.length || 1));
  };
   
useEffect(() => {
   // Clear timer cũ khi chuyển item hoặc cập nhật playlist
  if (timerRef.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }

  if (isImage || isWebView || (isVideo && currentPlaylistItem?.duration)) {
    timerRef.current = setTimeout(
      goNext,
      (currentPlaylistItem?.duration || 5) * 1000,
    );
  }

  return () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
}, [contentItems, currentPlaylistItem, isImage, isWebView, isVideo]);

  // Reset vị trí khi đổi playlist
  useEffect(() => {
    setIndex(0);
  }, [data]);

  const style: React.CSSProperties = {
    width: isMobile ? '100%' : isLandscape ? '100%' : 'auto',
    height: isMobile && isLandscape ? 'auto' : '100%',
    objectFit: 'cover',
    border: 0,
    aspectRatio: isMobile
      ? isLandscape
        ? '16 / 9'
        : '9 / 16'
      : undefined,
  };

  const renderMedia = () => {
    if (!currentPlaylistItem) return null;

    if (isImage) {
      return (
        <img
          key={`image-${currentPlaylistItem.url}-${index}`}
          src={currentPlaylistItem.url}
          style={style}
          alt=""
        />
      );
    }

    if (isVideo) {
      const hasDuration = (currentPlaylistItem?.duration ?? 0) > 0;

      return (
        <video
          key={`video-${currentPlaylistItem.url}-${index}`}
          src={currentPlaylistItem.url}
          autoPlay
          onEnded={hasDuration ? undefined : goNext}
          style={style}
        />
      );
    }

    if (isWebView) {
      return (
        <iframe
          key={`webview-${currentPlaylistItem.url}-${index}`}
          src={currentPlaylistItem.url}
          style={style}
          title="Preview content"
        />
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Container>
        <LoadingUI />
      </Container>
    );
  }
  if (!data && !loading) {
    return (
      <Container>
        <Text title_h9>
          No preview data found. Please make sure you have a valid preview key
          in the URL.
        </Text>
      </Container>
    );
  }

  return <Container>{renderMedia()}</Container>;
};

export default PreviewScreenPage;

const Container = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 100%;
    background-color: #000;
  }
`;